import React, { useEffect, useState } from "react";
import "./ReceiptLibrary.css";
import ActionButton from "../components/ActionButton.tsx";
import { supabase } from "../SupabaseClient.tsx";
import { useNavigate } from "react-router-dom";
import Order from "../types/Order.ts";
import { PiDownloadSimple, PiFileZip, PiSpinnerGap } from "react-icons/pi";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const ReceiptLibrary = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [interestRate, setInterestRate] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch orders from Supabase
    const fetchOrders = async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(
          "id, purchase_date, amount, status(name), origin(name), storage_path, name, notes, beneficiary, purchase_id, original_filename, created_at"
        )
        .order("created_at", { ascending: false });
      if (error) {
        console.error("Error fetching orders:", error);
      } else {
        setOrders(data);
      }
    };

    // Fetch interest rate from Supabase
    const fetchInterestRate = async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("interest_rate")
          .single(); // Assuming the current user is authenticated

        if (error) {
          console.error("Error fetching interest rate:", error);
        } else {
          setInterestRate(data.interest_rate);
        }
      } catch (error) {
        console.error("Unexpected error fetching interest rate:", error);
      }
    };

    fetchOrders();
    fetchInterestRate();
  }, []);

  // TODO: Change this to account for the reimburse schedule
  // const calculateRealizedValue = (amount: number, orderDate: string) => {
  //   if (!interestRate) return 0;

  //   const yearsSinceOrderDate =
  //     (new Date().getTime() - new Date(orderDate).getTime()) /
  //     (1000 * 60 * 60 * 24 * 365);
  //   return (
  //     amount * Math.pow(1 + interestRate / 100, yearsSinceOrderDate) - amount
  //   );
  // };

  const handleExportAllClicked = async () => {
    setExporting(true);
    const zip = new JSZip();

    // Fetch each file from Supabase storage and add to zip
    for (const order of orders) {
      if (order.storage_path && order.original_filename) {
        // Download file from Supabase storage
        const { data, error } = await supabase.storage
          .from("order_receipts")
          .download(order.storage_path);

        if (!error && data) {
          // Read file as ArrayBuffer
          const fileBlob = await data.arrayBuffer();
          zip.file(order.original_filename, fileBlob);
        }
      }
    }

    // Generate zip and trigger download
    zip
      .generateAsync({ type: "blob" })
      .then((content) => {
        saveAs(content, "receipts.zip");
        setExporting(false);
      })
      .catch(() => {
        setExporting(false);
      });
  };

  const handleRowClick = (id: string) => {
    // Find the selected order and pass it through navigation state
    const order = orders.find((order) => order.id === id);
    if (order) {
      navigate(`/receipt-library/${id}`, { state: { order } });
    }
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Receipt Library</h1>
            <p>View and manage your existing receipts.</p>
          </div>
          <ActionButton
            icon={exporting ? <PiSpinnerGap className="spin" /> : <PiFileZip />}
            text="Download All"
            onClick={handleExportAllClicked}
            responsive={true}
            secondary={true}
            disabled={exporting}
          />
        </div>
        <div className="recent-receipts-container">
          <div className="receipt-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Origin</th>
                  <th>Status</th>
                  <th>Beneficiary</th>
                  <th className="currency">Amount</th>
                  {/* <th className="currency">Realized Value</th> */}
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={index} onClick={() => handleRowClick(order.id)}>
                    <td>{order.name}</td>
                    <td>
                      {new Date(order.purchase_date).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          timeZone: "UTC",
                        }
                      )}
                    </td>
                    <td>{order.origin["name"]}</td>
                    <td>{order.status["name"]}</td>
                    <td>{order.beneficiary}</td>
                    <td className="currency">
                      {order.amount
                        ? `$${order.amount.toFixed(2).toLocaleString()}`
                        : ""}
                    </td>
                    {/* <td className="currency green">
                      {order.amount
                        ? `$${calculateRealizedValue(
                            order.amount,
                            order.purchase_date
                          ).toFixed(2)}`
                        : ""}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptLibrary;
